import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useWagmiWeb3 } from './WagmiWeb3Context';

const UnifiedWalletContext = createContext();

export const useUnifiedWallet = () => {
  const ctx = useContext(UnifiedWalletContext);
  if (!ctx) throw new Error('useUnifiedWallet must be used within UnifiedWalletProvider');
  return ctx;
};

export const WALLET_TYPES = { METAMASK: 'metamask' };
export const CHAIN_TYPES = { EVM: 'evm' };

export const UnifiedWalletProvider = ({ children }) => {
  const evmWallet = useWagmiWeb3();
  const evmConnected = !!evmWallet.isConnected;

  const activeWallet = evmConnected ? WALLET_TYPES.METAMASK : null;
  const activeChainType = evmConnected ? CHAIN_TYPES.EVM : null;

  const currentWallet = useMemo(() => {
    if (!evmConnected) return null;
    return {
      type: WALLET_TYPES.METAMASK,
      chainType: CHAIN_TYPES.EVM,
      account: evmWallet.account,
      network: evmWallet.network,
      isNetworkSupported: evmWallet.isNetworkSupported,
      error: evmWallet.error,
    };
  }, [evmConnected, evmWallet.account, evmWallet.network, evmWallet.isNetworkSupported, evmWallet.error]);

  const connectWallet = useCallback(async () => {
    return await evmWallet.connectWallet();
  }, [evmWallet]);

  const disconnectWallet = useCallback(() => {
    if (evmConnected) evmWallet.disconnectWallet();
  }, [evmConnected, evmWallet]);

  const disconnectChain = disconnectWallet;

  const walletForChain = useCallback(() => currentWallet, [currentWallet]);

  const getAvailableWallets = useCallback(() => ([{
    type: WALLET_TYPES.METAMASK,
    name: 'MetaMask',
    chainType: CHAIN_TYPES.EVM,
    icon: '🦊',
    isInstalled: evmWallet.isMetaMaskInstalled?.() ?? true,
    isConnected: evmWallet.isConnected,
    account: evmWallet.account,
    network: evmWallet.network,
    isNetworkSupported: evmWallet.isNetworkSupported,
    supportedNetworks: evmWallet.supportedNetworks,
  }]), [evmWallet]);

  const formatAddress = useCallback((address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }, []);

  const getTransactionMethods = useCallback(() => {
    if (!currentWallet) return null;
    return {
      sendTransaction: (txData) => currentWallet.signer?.sendTransaction(txData),
      getBalance: (address) => currentWallet.provider?.getBalance(address || currentWallet.account),
    };
  }, [currentWallet]);

  const value = useMemo(() => ({
    activeWallet,
    activeChainType,
    isConnected: evmConnected,
    isNetworkSupported: evmWallet.isNetworkSupported,
    currentWallet,
    connectWallet,
    disconnectWallet,
    disconnectChain,
    walletForChain,
    getAvailableWallets,
    formatAddress,
    getTransactionMethods,
    evmWallet,
    WALLET_TYPES,
    CHAIN_TYPES,
  }), [activeWallet, activeChainType, evmConnected, evmWallet, currentWallet, connectWallet, disconnectWallet, disconnectChain, walletForChain, getAvailableWallets, formatAddress, getTransactionMethods]);

  return (
    <UnifiedWalletContext.Provider value={value}>
      {children}
    </UnifiedWalletContext.Provider>
  );
};
